import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth, getAuthHeaders } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { Checkbox } from '../ui/checkbox';
import { Package, Warning, CheckCircle, Clock, X, Trash } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString('fr-FR');
    const time = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    return `${date} à ${time.replace(':', 'h')}`;
  } catch {
    return '—';
  }
};

export default function StockNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState({ pending: [], processed: [] });
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [confirmIgnore, setConfirmIgnore] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isAdmin = user?.role === 'admin';

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/api/stock/notifications`, { headers: getAuthHeaders() });
      setNotifications(res.data);
    } catch {
      toast.error('Erreur chargement notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleProcess = async (id) => {
    setProcessing((p) => ({ ...p, [id]: true }));
    try {
      await axios.post(`${API}/api/stock/notifications/${id}/process`, {}, { headers: getAuthHeaders() });
      toast.success('Stock mis à jour');
      fetchNotifications();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du traitement');
    } finally {
      setProcessing((p) => ({ ...p, [id]: false }));
    }
  };

  const doIgnore = async (id) => {
    try {
      await axios.post(`${API}/api/stock/notifications/${id}/ignore`, {}, { headers: getAuthHeaders() });
      toast.success('Notification ignorée');
      setConfirmIgnore(null);
      fetchNotifications();
    } catch {
      toast.error('Erreur');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const doBulkDelete = async () => {
    try {
      await axios.post(
        `${API}/api/stock/notifications/bulk-delete`,
        { ids: Array.from(selectedIds) },
        { headers: getAuthHeaders() }
      );
      toast.success(`${selectedIds.size} ligne(s) supprimée(s)`);
      setSelectedIds(new Set());
      setConfirmDelete(false);
      fetchNotifications();
    } catch {
      toast.error('Erreur suppression');
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Chargement...</div>;
  }

  const { pending, processed } = notifications;

  return (
    <div className="space-y-6">
      {/* PENDING */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Warning size={20} className="text-amber-600" />
          <h2 className="text-lg font-semibold text-gray-900">
            À traiter <span className="text-amber-600">({pending.length})</span>
          </h2>
        </div>

        {pending.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
            Aucune mise à jour de stock en attente
          </div>
        ) : (
          <div className="space-y-2">
            {pending.map((n) => {
              const incomplete = !n.has_dimension || !n.has_quantity;
              return (
                <div
                  key={n.id}
                  className="bg-white rounded-lg border border-amber-200 p-4 flex items-center gap-4"
                  data-testid={`stock-pending-${n.id}`}
                >
                  <Package size={20} className="text-amber-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">{n.client_name}</div>
                    <div className="text-xs text-gray-500 flex gap-3 flex-wrap">
                      <span>Intervention : {formatDateTime(n.intervention_date)}</span>
                      {n.dimension && <span>• {n.dimension}</span>}
                      {n.tire_brand && <span>• {n.tire_brand}</span>}
                      {n.quantity && <span>• Qté {n.quantity}</span>}
                      {n.tire_type && <span>• {n.tire_type}</span>}
                    </div>
                    {incomplete && (
                      <div className="text-xs text-red-600 mt-1">
                        ⚠ Informations incomplètes
                      </div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleProcess(n.id)}
                    disabled={incomplete || processing[n.id]}
                    className="bg-amber-500 hover:bg-amber-600 text-white"
                    data-testid={`stock-process-${n.id}`}
                  >
                    {processing[n.id] ? '...' : 'Traiter'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirmIgnore(n)}
                    className="border-gray-300 text-gray-600 hover:bg-gray-50"
                    title="Ignorer"
                    data-testid={`stock-ignore-${n.id}`}
                  >
                    <X size={14} />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* PROCESSED */}
      {processed.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CheckCircle size={20} className="text-green-600" />
              <h2 className="text-lg font-semibold text-gray-900">Traitées / Ignorées récemment</h2>
            </div>
            {isAdmin && selectedIds.size > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirmDelete(true)}
                className="gap-1.5 border-red-300 text-red-600 hover:bg-red-50"
                data-testid="stock-bulk-delete-btn"
              >
                <Trash size={14} />
                Supprimer la sélection ({selectedIds.size})
              </Button>
            )}
          </div>
          <div className="space-y-1">
            {processed.map((n) => (
              <div
                key={n.id}
                className={`bg-white rounded-lg border ${n.ignored ? 'border-gray-200' : 'border-green-200'} p-3 flex items-center gap-3`}
                data-testid={`stock-processed-${n.id}`}
              >
                {isAdmin && (
                  <Checkbox
                    checked={selectedIds.has(n.id)}
                    onCheckedChange={() => toggleSelect(n.id)}
                    data-testid={`stock-select-${n.id}`}
                  />
                )}
                {n.ignored ? (
                  <X size={16} className="text-gray-500 flex-shrink-0" />
                ) : (
                  <CheckCircle size={16} className="text-green-600 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{n.client_name}</div>
                  <div className="text-xs text-gray-500 flex gap-3 flex-wrap">
                    {n.dimension && <span>{n.dimension}</span>}
                    {n.tire_brand && <span>• {n.tire_brand}</span>}
                    {n.quantity && <span>• Qté {n.quantity}</span>}
                    <span>• Intervention : {formatDateTime(n.intervention_date)}</span>
                  </div>
                </div>
                <div className="text-xs text-gray-500 flex items-center gap-1 flex-shrink-0">
                  <Clock size={12} />
                  {n.ignored ? 'Ignorée' : 'Mise à jour effectuée'} le {formatDateTime(n.stock_updated_at)}
                  {n.stock_updated_by_name && ` par ${n.stock_updated_by_name}`}
                </div>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${n.ignored ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-700'}`}>
                  {n.ignored ? 'Ignorée' : 'Traitée'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Ignore confirmation */}
      <Dialog open={!!confirmIgnore} onOpenChange={(o) => !o && setConfirmIgnore(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ignorer cette mise à jour de stock ?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Cette notification sera déplacée vers la section "Traitées" avec le statut "Ignorée".
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmIgnore(null)}>Annuler</Button>
            <Button
              onClick={() => doIgnore(confirmIgnore?.id)}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="stock-confirm-ignore"
            >
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk delete confirmation */}
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Supprimer {selectedIds.size} ligne(s) sélectionnée(s) ?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Cette action est permanente — les lignes disparaîtront de la section "Traitées".
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(false)}>Annuler</Button>
            <Button
              onClick={doBulkDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="stock-confirm-delete"
            >
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
